/**
 * The "Available models" toggle above the model catalog.
 *
 * One button: hide the models this install cannot use, because no set-up
 * supplier offers them. Pressed by default.
 *
 * **It owns no filter state**, for the same reason as `OriginToggle`: the page
 * reads it from the filter list and writes the one condition back. Here the
 * condition is the exception rather than the rule. Hiding is what happens when
 * there is NO availability condition, so a view saved before this toggle
 * existed opens with the unusable models hidden too, and turning the toggle off
 * adds the condition that lets them back in.
 *
 * **While they are shown, a line says what they wait on.** "Needs a key:
 * Anthropic (22), Google (48)." names each supplier whose missing key leaves
 * models unusable, and the hosted supplier apart as "Not connected", because
 * what it needs is a connection rather than a key. The page counts them under
 * the same rule as the number on the button.
 */
import { Info } from 'lucide-react'
import { Tooltip } from '@/components/ui'

/** One supplier on the "Needs a key" line, and how many models wait on it. */
export interface SupplierWait {
  name: string
  count: number
}

export interface AvailabilityToggleProps {
  /** Whether unusable models are hidden (the default). */
  hiding: boolean
  /** How many unusable models match every other filter. */
  hiddenCount: number
  /** Suppliers whose missing key leaves models unusable, in display order. */
  needsKey: readonly SupplierWait[]
  /** The hosted supplier when this install isn't connected to it. */
  notConnected: readonly SupplierWait[]
  onToggle: (hide: boolean) => void
}

/** "Anthropic (22), Google (48)". */
function listWaits(waits: readonly SupplierWait[]): string {
  return waits.map(w => `${w.name} (${w.count})`).join(', ')
}

export function AvailabilityToggle({ hiding, hiddenCount, needsKey, notConnected, onToggle }: AvailabilityToggleProps) {
  const showWaits = !hiding && (needsKey.length > 0 || notConnected.length > 0)
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2" data-testid="availability-toggle">
        <span className="text-sm">Available models</span>
        <button
          type="button"
          aria-pressed={hiding}
          onClick={() => onToggle(!hiding)}
          className={[
            'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
            hiding
              ? 'bg-primary/10 text-primary border-primary/45'
              : 'bg-background text-muted-foreground border-border hover:bg-muted',
          ].join(' ')}
        >
          <span>Hide models you can't use</span>
          <span className="font-normal tabular-nums opacity-75">{hiddenCount}</span>
        </button>
        <Tooltip
          triggerLabel="About models you can't use"
          content={
            <span>
              Models from suppliers this install isn't set up for, or that their supplier no longer
              lists. To use a supplier's models, set it up in this install's settings file and restart.
            </span>
          }
        >
          <Info aria-hidden="true" className="h-3.5 w-3.5 text-muted-foreground/70 cursor-help" />
        </Tooltip>
      </div>
      {showWaits && (
        <p className="text-xs text-foreground/80" data-testid="missing-setup">
          {needsKey.length > 0 && <><b className="font-semibold">Needs a key:</b> {listWaits(needsKey)}.</>}
          {needsKey.length > 0 && notConnected.length > 0 && ' '}
          {notConnected.length > 0 && <><b className="font-semibold">Not connected:</b> {listWaits(notConnected)}.</>}
        </p>
      )}
    </div>
  )
}
